import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import {
  Directive,
  ElementRef,
  OnInit,
  NgZone,
  inject,
  DestroyRef,
  input,
} from '@angular/core';
import { fromEvent } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/**
 * Directive that synchronizes horizontal scrolling between two elements.
 * When the target element (specified by scrollWatch) is scrolled horizontally,
 * the source element's scroll position is updated to match.
 */
@Directive({ selector: '[tableScrollSync]' })
export class Ag1TableScrollSyncDirective implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _el = inject(ElementRef);
  private _ngZone = inject(NgZone);
  /**
   * The virtual scroll viewport to watch for scroll events.
   * This element's horizontal scroll position will be synchronized with the source element.
   */
  readonly scrollWatch = input<CdkVirtualScrollViewport>(undefined);

  /** The element that will mirror the scroll position */
  private _sourceElement: HTMLElement;
  /** The element being watched for scroll events */
  private _targetElement: HTMLElement;
  /** Tracks the last known scroll position to prevent unnecessary updates */
  private _lastKnownScrollLeft: number = 0;
  /** Stores the current animation frame ID for cleanup */
  private _frameId: number | null = null;

  constructor() {}

  ngOnInit(): void {
    if (!this.scrollWatch()) {
      console.error('TableScrollSync: scrollWatch is required');
      return;
    }

    this._initializeElements();
    this._setupScrollSync();
    this._setupContentResizeSync();
  }

  /**
   * When the header content shrinks, for example when bulk actions replace the
   * header cells, the browser clamps its scroll position to zero. Nothing resets
   * it when the content grows again, so we resync on every content resize.
   */
  private _setupContentResizeSync(): void {
    const content: Element | null = this._sourceElement.firstElementChild;
    if (!content) {
      return;
    }

    this._ngZone.runOutsideAngular(() => {
      const observer = new ResizeObserver(() => this._syncScrollPosition());
      observer.observe(content);
      this._destroyRef.onDestroy(() => observer.disconnect());
    });
  }

  private _syncScrollPosition(): void {
    const currentScrollLeft: number = this._targetElement.scrollLeft;
    this._sourceElement.scrollLeft = currentScrollLeft;
    this._lastKnownScrollLeft = currentScrollLeft;
  }

  private _initializeElements(): void {
    this._sourceElement = this._el.nativeElement;
    this._targetElement = this.scrollWatch().elementRef.nativeElement;
  }

  private _setupScrollSync(): void {
    // Performance optimisation: Avoid angular change detection
    this._ngZone.runOutsideAngular(() => {
      // Performance optimisation: use passive event listener to avoid blocking the main thread
      // https://stackoverflow.com/a/78265405
      fromEvent(this._targetElement, 'scroll', { passive: true })
        .pipe(takeUntilDestroyed(this._destroyRef))
        .subscribe(() => {
          const currentScrollLeft: number = this._targetElement.scrollLeft;

          // If there's no change in scroll position, don't proceed
          if (currentScrollLeft === this._lastKnownScrollLeft) {
            return;
          }

          // Performance optimisation: Use requestAnimationFrame to avoid layout thrashing and repaints
          this._frameId = requestAnimationFrame(() => {
            this._syncScrollPosition();
            this._frameId = null;
          });
        });
    });
  }

  /**
   * Hack to force a recalculation of column widths.
   * This is necessary because the column widths are calculated based on the
   * scroll position of the target element, and the scroll position is not
   * updated when the columns are dropped.
   */
  public forceSync(): void {
    if (!this._targetElement || this._frameId !== null) {
      return; // Prevent multiple simultaneous calls
    }

    this._ngZone.runOutsideAngular(() => {
      const originalScrollLeft: number = this._targetElement.scrollLeft;

      // Choose nudge direction based on current position
      const nudgeAmount: number = originalScrollLeft > 0 ? -1 : 1;

      // Apply nudge
      this._targetElement.scrollLeft = originalScrollLeft + nudgeAmount;

      // Restore original position on next frame
      this._frameId = requestAnimationFrame(() => {
        this._targetElement.scrollLeft = originalScrollLeft;
        this._frameId = null;
      });
    });
  }
}
